import { QuizCard } from './QuizCard';
import { Flex } from '@chakra-ui/react';
import { StageCard } from './StageCard';
import { betweenEach } from '~/framework';
import { Stage, Assessment } from './Stage';
import { WikiSearchModal } from './WikiSearchModal';
import { MoveStageConrol } from './MoveStageControl';
import { GuideSearchModal } from './GuideSearchModal';
import { createContext, useContext, useState } from 'react';
import { useLoggedInUser, useSiteInfo } from '~/pages/_shared';
import { CardList, NestedCardContainer } from '~/ui-components';
import { BorderedAddButton, ModestAddButton } from './AddButton';
import { EditSignoffCard } from './EditSignOffCard';
import { ExternalModuleSearchModal } from './ExternalModuleSearchModal';
import { StageEditorInterface, StageEditorState } from './StageEditorInterface';

export const StageEditorContext = createContext<null | StageEditorInterface>(null);

export type StageEditorProps = {
   value: Stage[];
   onChange: (stages: Stage[]) => any;
};

export function StageEditor({ value: stages, onChange }: StageEditorProps) {
   const { data: siteInfo } = useSiteInfo();

   const [state, setState] = useState<StageEditorState>({
      selectedStageIndex: null,
      openModal: null,
   });

   const { data: user } = useLoggedInUser();

   const stageEditor = new StageEditorInterface({
      stages: stages,
      onStagesChange: onChange,

      state: state,
      onStateChange: setState,

      siteSettings: siteInfo || {},

      user: user,
   });

   return (
      <StageEditorContext.Provider value={stageEditor}>
         <CardList data-testid="stage-list">
            <BorderedAddButton index={0} />
            {betweenEach(
               stages.map(stage => (
                  <Flex direction="column" key={stageEditor.getUniqueIdForStage(stage)}>
                     <Flex alignItems="center">
                        <MoveStageConrol stage={stage} />
                        <StageCard stage={stage} />
                     </Flex>
                     {stage.docSummaryInfo?.attachedQuiz &&
                        stage.docSummaryInfo?.attachedQuiz.enabled &&
                        siteInfo?.['dev-feature-quizzes-v2'] && (
                           <NestedCardContainer
                              key={stage.docSummaryInfo.attachedQuiz.quizid}
                              mt={4}
                              mb={2}
                           >
                              <QuizCard quiz={stage.docSummaryInfo.attachedQuiz} />
                           </NestedCardContainer>
                        )}
                     {stage.assessments?.map((assessment: Assessment) => (
                        <NestedCardContainer key={assessment.assessmentid} mt={4} ml={6}>
                           <EditSignoffCard assessment={assessment} />
                        </NestedCardContainer>
                     ))}
                  </Flex>
               )),
               index => (
                  <ModestAddButton index={index + 1} key={`add-button-${index}`} />
               )
            )}
            {stages.length > 0 && <BorderedAddButton index={stages.length} />}
         </CardList>
         <GuideSearchModal />
         {stageEditor.isETMFeatureActive && user?.permissions.canIManageExternalTrainingModules && (
            <ExternalModuleSearchModal />
         )}
         <WikiSearchModal />
      </StageEditorContext.Provider>
   );
}

export function useStageEditor() {
   const stageEditor = useContext(StageEditorContext);
   if (!stageEditor) throw Error('Cannot call useStageEditor outside of StageEditor');
   return stageEditor;
}
